import type {
  OfferConversionMode,
  OfferEmbedProvider,
  OfferTemplate,
  OfferTemplateContent,
  OfferTemplateContentInput,
  StripePriceOptionInput,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { getReadableTextColor } from '@/utils/colors';

export type OfferEmbedRender =
  | { kind: 'iframe'; provider: OfferEmbedProvider; src: string }
  | { kind: 'whop-checkout'; checkoutPlanId: string };

// L'iframe est sur le domaine du service : allow-same-origin ne lui donne aucun accès à notre page.
// Sans allow-top-navigation, elle ne peut pas rediriger le live.
export const OFFER_EMBED_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

export const OFFER_EMBED_IFRAME_ALLOW = 'payment; clipboard-write';

// Mêmes hôtes que le back : pas de sous-domaine, www redirige vers kalleo.io.
const KALLEO_HOSTS = new Set(['kalleo.io', 'www.kalleo.io']);

const PROVIDER_HOSTS: Record<
  Exclude<OfferEmbedProvider, 'STRIPE_PRICING_TABLE'>,
  (host: string) => boolean
> = {
  CALENDLY: (host) => isHostOrSubdomain(host, 'calendly.com'),
  ICLOSED: (host) => isHostOrSubdomain(host, 'iclosed.io'),
  TYPEFORM: (host) => host === 'form.typeform.com' || host === 'form.typeform.eu',
  WHOP: (host) => isHostOrSubdomain(host, 'whop.com'),
  KALLEO: (host) => KALLEO_HOSTS.has(host.toLowerCase()),
};

const WHOP_CHECKOUT_PLAN_ID = /^plan_[A-Za-z0-9]+$/;

type OfferConversionFields = Pick<
  OfferTemplateContent,
  | 'conversionMode'
  | 'buttonLink'
  | 'embedProvider'
  | 'embedUrl'
  | 'checkoutPlanId'
  | 'embedFallbackUrl'
>;

export function isEmbedOffer(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return content?.conversionMode === 'EMBED';
}

export function isStripeCheckoutOffer(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return content?.conversionMode === 'STRIPE_EMBEDDED_CHECKOUT';
}

// Code embed comme paiement Stripe s'affichent dans le panneau, à la place du chat.
export function opensInOfferPanel(
  content: Pick<OfferTemplateContent, 'conversionMode'> | null | undefined,
): boolean {
  return isEmbedOffer(content) || isStripeCheckoutOffer(content);
}

export const OFFER_EMBED_CODE_MAX_LENGTH = 10000;

type OfferConversionInput = Pick<
  OfferTemplateContentInput,
  | 'conversionMode'
  | 'buttonLink'
  | 'embedCode'
  | 'stripePriceId'
  | 'stripePrices'
  | 'embedProvider'
  | 'embedUrl'
>;

// Lignes vides du formulaire écartées. Le back garde aussi le premier prix dans `stripePriceId`.
function normalizeStripePriceOptions(
  stripePrices: StripePriceOptionInput[] | null | undefined,
): StripePriceOptionInput[] {
  return (stripePrices ?? [])
    .map((option) => ({
      priceId: option.priceId.trim(),
      installmentCount: option.installmentCount ?? null,
    }))
    .filter((option) => option.priceId.length > 0);
}

// Le back n'accepte que la donnée du mode choisi : un lien, un code ou des prix Stripe. Pour
// Kalleo, il n'accepte que l'URL publique de l'offre, jamais de code.
export function toOfferConversionInput(
  conversionMode: OfferConversionMode | undefined,
  buttonLink: string | null | undefined,
  embedCode: string | null | undefined,
  stripePrices?: StripePriceOptionInput[] | null,
): OfferConversionInput {
  if (conversionMode === 'EMBED') {
    const kalleoUrl = embedCode ? extractKalleoOfferUrl(embedCode) : null;
    if (kalleoUrl) {
      return {
        conversionMode: 'EMBED',
        buttonLink: null,
        embedCode: null,
        stripePriceId: null,
        embedProvider: 'KALLEO',
        embedUrl: kalleoUrl,
      };
    }
    return {
      conversionMode: 'EMBED',
      buttonLink: null,
      embedCode: embedCode?.trim() || null,
      stripePriceId: null,
    };
  }
  if (conversionMode === 'STRIPE_EMBEDDED_CHECKOUT') {
    const options = normalizeStripePriceOptions(stripePrices);
    return {
      conversionMode: 'STRIPE_EMBEDDED_CHECKOUT',
      buttonLink: null,
      embedCode: null,
      stripePriceId: options[0]?.priceId ?? null,
      stripePrices: options,
    };
  }
  return {
    conversionMode: 'EXTERNAL_LINK',
    buttonLink: buttonLink?.trim() ?? '',
    embedCode: null,
    stripePriceId: null,
  };
}

// Ce que le champ « code d'intégration » affiche pour une offre enregistrée : le code collé, ou
// l'URL de l'offre pour Kalleo, que le back garde à la place du code.
export function getOfferEmbedCodeValue(
  content:
    Pick<OfferTemplateContent, 'embedProvider' | 'embedCode' | 'embedUrl'> | null | undefined,
): string {
  if (content?.embedProvider === 'KALLEO') return content.embedUrl ?? '';
  return content?.embedCode ?? '';
}

// L'admin colle l'URL de son offre ou le code fourni par Kalleo (<div data-kalleo>, ou l'iframe
// que crée son script) : on n'en garde que l'URL, le script n'est jamais chargé dans le live.
export function extractKalleoOfferUrl(embedCode: string): string | null {
  const value = embedCode.trim();
  if (!value) return null;
  if (!value.startsWith('<')) return isKalleoOfferUrl(value) ? value : null;
  // DOMParser n'exécute aucun script du code analysé.
  const document = new DOMParser().parseFromString(value, 'text/html');
  const candidate =
    document.querySelector('[data-kalleo]')?.getAttribute('data-kalleo') ??
    document.querySelector('iframe[src]')?.getAttribute('src');
  const url = candidate?.trim();
  return url && isKalleoOfferUrl(url) ? url : null;
}

function isKalleoOfferUrl(value: string): boolean {
  const url = parseHttpsUrl(value);
  return url !== null && KALLEO_HOSTS.has(url.hostname) && url.pathname.length > 1;
}

// Formules d'une offre enregistrée, telles que le formulaire et la duplication les renvoient.
// Une offre d'avant les formules multiples n'a que `stripePriceId`.
export function getStripePriceOptionInputs(
  content: Pick<OfferTemplateContent, 'stripePriceId' | 'stripePrices'> | null | undefined,
): StripePriceOptionInput[] {
  if (content?.stripePrices?.length) {
    return content.stripePrices.map((option) => ({
      priceId: option.priceId,
      installmentCount: option.installmentCount,
    }));
  }
  return content?.stripePriceId ? [{ priceId: content.stripePriceId, installmentCount: null }] : [];
}

// Recopie d'une offre existante (duplication) : le back réextrait le code et revérifie les prix.
export function copyOfferConversionInput(
  content: Pick<
    OfferTemplateContent,
    | 'conversionMode'
    | 'buttonLink'
    | 'embedCode'
    | 'embedFallbackUrl'
    | 'stripePriceId'
    | 'stripePrices'
    | 'embedProvider'
    | 'embedUrl'
  >,
): OfferConversionInput & Pick<OfferTemplateContentInput, 'embedFallbackUrl'> {
  return {
    ...toOfferConversionInput(
      content.conversionMode,
      content.buttonLink,
      getOfferEmbedCodeValue(content),
      getStripePriceOptionInputs(content),
    ),
    embedFallbackUrl: content.embedFallbackUrl ?? null,
  };
}

const EMBED_CODE_REJECTED_ERRORS = new Set([
  'HTTP.ERROR.400.OFFER.CONTENT.EMBED_CODE.INVALID',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.INVALID',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.REQUIRED',
  'HTTP.ERROR.400.OFFER.CONTENT.KALLEO_URL.TOO_LONG',
]);

// Le back renvoie le refus du code (ou de l'URL Kalleo) dans le détail d'une erreur de validation
// globale.
export function isOfferEmbedCodeRejected(
  error: { message?: string; details?: unknown } | null | undefined,
): boolean {
  if (typeof error?.message === 'string' && EMBED_CODE_REJECTED_ERRORS.has(error.message)) {
    return true;
  }
  if (!Array.isArray(error?.details)) return false;
  return error.details.some((detail: unknown) => {
    const message =
      typeof detail === 'object' && detail !== null
        ? (detail as { message?: unknown }).message
        : undefined;
    return typeof message === 'string' && EMBED_CODE_REJECTED_ERRORS.has(message);
  });
}

// Lien externe historique : un lien saisi sans schéma s'ouvre en https.
export function resolveOfferExternalUrl(buttonLink: string | null | undefined): string | null {
  const link = buttonLink?.trim();
  if (!link) return null;
  return link.startsWith('http://') || link.startsWith('https://') ? link : `https://${link}`;
}

// Le back a déjà extrait et validé ces valeurs ; on revérifie l'hôte avant de construire l'iframe.
export function resolveOfferEmbed(
  content: OfferConversionFields | null | undefined,
  embedDomain: string,
): OfferEmbedRender | null {
  if (!content || !isEmbedOffer(content) || !content.embedProvider) return null;
  const provider = content.embedProvider;

  // Le Pricing Table Stripe envoie l'onglet entier vers Checkout : il ferait quitter le live.
  if (provider === 'STRIPE_PRICING_TABLE') return null;

  const checkoutPlanId = content.checkoutPlanId?.trim() ?? '';
  if (provider === 'WHOP' && WHOP_CHECKOUT_PLAN_ID.test(checkoutPlanId)) {
    return { kind: 'whop-checkout', checkoutPlanId };
  }

  const url = parseHttpsUrl(content.embedUrl);
  if (!url) return null;
  // Un service hors liste (ex. une iframe générique enregistrée avant son retrait) n'est pas affiché.
  if (!PROVIDER_HOSTS[provider]?.(url.hostname)) return null;
  if (provider === 'KALLEO' && url.pathname.length <= 1) return null;

  if (provider === 'CALENDLY') {
    // Paramètres que le widget officiel ajoute lui-même pour un affichage intégré.
    url.searchParams.set('embed_domain', embedDomain);
    url.searchParams.set('embed_type', 'Inline');
  }
  return { kind: 'iframe', provider, src: url.toString() };
}

export function resolveOfferEmbedFallbackUrl(
  content: Pick<OfferTemplateContent, 'embedFallbackUrl'> | null | undefined,
): string | null {
  return parseHttpsUrl(content?.embedFallbackUrl)?.toString() ?? null;
}

function parseHttpsUrl(value: string | null | undefined): URL | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  // URL vide le port 443 par défaut : tout port restant est non standard.
  if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
  return url;
}

function isHostOrSubdomain(host: string, domain: string): boolean {
  const normalized = host.toLowerCase();
  return normalized === domain || normalized.endsWith(`.${domain}`);
}

// Iframe que crée le script officiel de Whop, qui ne fonctionne pas isolé du live : on la construit
// nous-mêmes pour ne pas exécuter ce script dans notre page. Whop ne reçoit que l'origine du live,
// jamais son URL, qui porte le jeton du spectateur.
export function buildWhopCheckoutFrameSrc(
  checkoutPlanId: string,
  liveOrigin: string,
  theme: 'light' | 'dark',
): string {
  const url = new URL(`https://whop.com/embedded/checkout/${encodeURIComponent(checkoutPlanId)}/`);
  url.searchParams.set('h', liveOrigin);
  url.searchParams.set('v', '3');
  url.searchParams.set('theme', theme);
  return url.toString();
}

// Le panneau est posé sur le fond de l'identité visuelle, blanc par défaut : sans thème (identité
// absente ou ancienne), on le déduit de ce fond pour garder titre et croix lisibles.
export function isLightOfferPanel(
  visualIdentity: Pick<WorkspaceVisualIdentity, 'theme' | 'backgroundColor'> | null | undefined,
): boolean {
  if (visualIdentity?.theme) return visualIdentity.theme === 'LIGHT';
  return getReadableTextColor(visualIdentity?.backgroundColor ?? '#FFFFFF') === '#000000';
}

// Aperçu du back-office : le panneau du live, alimenté par la variante renvoyée par test-embed.
export function buildOfferEmbedPreview(
  templateId: number,
  content: OfferTemplateContent,
): OfferTemplate {
  return {
    id: templateId,
    webinarId: 0,
    workspaceId: 0,
    privateTitle: '',
    abTesting: false,
    splitPercentA: null,
    contents: [content],
  };
}

// L'embed n'est affiché que pour l'offre encore présentée sur laquelle le spectateur a cliqué.
export function shouldShowOfferEmbed(
  openedOfferEmbedId: number | null,
  offer: Pick<OfferTemplate, 'id' | 'contents'> | null | undefined,
): boolean {
  return (
    openedOfferEmbedId !== null &&
    offer?.id === openedOfferEmbedId &&
    opensInOfferPanel(offer.contents?.[0])
  );
}

// Hors panneau (replay, intervenants) : lien de repli, ou à défaut l'URL validée de l'iframe.
export function resolveOfferEmbedTabUrl(
  content: OfferConversionFields | null | undefined,
): string | null {
  const fallbackUrl = resolveOfferEmbedFallbackUrl(content);
  if (fallbackUrl) return fallbackUrl;
  if (resolveOfferEmbed(content, window.location.host)?.kind !== 'iframe') return null;
  return parseHttpsUrl(content?.embedUrl)?.toString() ?? null;
}

export type OfferEmbedCodeIssue =
  'unsupportedProvider' | 'stripePricingTable' | 'whopOptions' | 'kalleoUrl';

// Seuls l'identifiant de plan et le thème (qui suit celui du live) sont repris dans le live.
const SUPPORTED_WHOP_CHECKOUT_ATTRIBUTES = new Set([
  'data-whop-checkout-plan-id',
  'data-whop-checkout-theme',
]);

// Garde-fou du formulaire sur ce que le back accepte mais que le live n'affiche pas fidèlement :
// une iframe vers n'importe quel domaine (GENERIC_IFRAME), le Pricing Table Stripe et les options
// Whop non reprises. Même ordre de détection que l'extracteur back.
// DOMParser n'exécute aucun script du code analysé.
export function getOfferEmbedCodeIssue(embedCode: string): OfferEmbedCodeIssue | null {
  if (extractKalleoOfferUrl(embedCode)) return null;
  // Code qui vise Kalleo sans URL d'offre acceptée (http, sous-domaine, page d'accueil…).
  if (/kalleo\.io/i.test(embedCode)) return 'kalleoUrl';
  const document = new DOMParser().parseFromString(embedCode, 'text/html');
  if (document.querySelector('stripe-pricing-table')) return 'stripePricingTable';
  const whopCheckout = document.querySelector('[data-whop-checkout-plan-id]');
  if (whopCheckout) {
    const hasUnsupportedOption = whopCheckout
      .getAttributeNames()
      .some(
        (name) =>
          name.startsWith('data-whop-checkout-') && !SUPPORTED_WHOP_CHECKOUT_ATTRIBUTES.has(name),
      );
    return hasUnsupportedOption ? 'whopOptions' : null;
  }
  if (document.querySelector('[data-tf-widget], [data-tf-live]')) return null;
  for (const attribute of ['data-url', 'data-tf-url']) {
    const value = document.querySelector(`[${attribute}]`)?.getAttribute(attribute);
    if (isKnownProviderUrl(value)) return null;
  }
  const iframeSrc = document.querySelector('iframe[src]')?.getAttribute('src');
  return parseHttpsUrl(iframeSrc) !== null && !isKnownProviderUrl(iframeSrc)
    ? 'unsupportedProvider'
    : null;
}

function isKnownProviderUrl(value: string | null | undefined): boolean {
  const url = parseHttpsUrl(value);
  return (
    url !== null && Object.values(PROVIDER_HOSTS).some((matchesHost) => matchesHost(url.hostname))
  );
}
